import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import AnimatedSection from "./AnimatedSection";
import { Headset } from "lucide-react";

const HowItWorks = () => {
  const { lang } = useLang();
  const t = translations[lang].howItWorks;

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-slate-50 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {/* Mobile image */}
        <AnimatedSection>
          <img src={t.image} alt="Port logistics" data-testid="howitworks-image-mobile"
            className="lg:hidden rounded-2xl w-full h-[200px] sm:h-[260px] object-cover shadow-lg mb-10" loading="lazy" />
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <AnimatedSection>
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric mb-4 font-body">{t.overline}</p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-navy font-heading mb-12">{t.headline}</h2>
            </AnimatedSection>
            <div className="relative">
              <div className="absolute left-5 md:left-6 top-0 bottom-0 w-[2px] bg-gradient-to-b from-electric via-electric/40 to-transparent" />
              <div className="space-y-6">
                {t.steps.map((step, i) => (
                  <TimelineStep key={i} step={step} index={i} />
                ))}
              </div>
            </div>

            {/* Promise - bold statement, not a note */}
            <AnimatedSection delay={0.7}>
              <div data-testid="how-it-works-promise" className="mt-10 ml-14 relative">
                <div className="bg-navy rounded-2xl p-6 flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Headset size={20} className="text-emerald-400" />
                  </div>
                  <p className="text-white font-semibold text-base leading-relaxed font-heading">
                    {t.promise}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Desktop image */}
          <AnimatedSection delay={0.3}>
            <img src={t.image} alt="Port logistics operations" data-testid="howitworks-image-desktop"
              className="hidden lg:block sticky top-28 rounded-2xl w-full h-[600px] object-cover shadow-xl" loading="lazy" />
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

const TimelineStep = ({ step, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }} data-testid={`timeline-step-${index}`} className="flex gap-4 md:gap-5">
      <div className="shrink-0 relative z-10">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-electric flex items-center justify-center shadow-lg shadow-electric/30">
          <span className="text-white font-bold text-xs md:text-sm font-heading">{step.number}</span>
        </div>
      </div>
      <div className="pt-1 pb-2">
        <h3 className="text-base md:text-lg font-bold text-navy mb-1 font-heading">{step.title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed font-body">{step.desc}</p>
      </div>
    </motion.div>
  );
};

export default HowItWorks;
